import { apiRequest } from '../lib/api-client';

/** Domains, forms, pages and e-mail (phases 12, 13, 14, 16). */

export interface DomainView {
  id: string;
  hostname: string;
  kind: 'SUBDOMAIN' | 'CUSTOM';
  phase: string;
  ownership: string;
  certificate: string;
  isPrimary: boolean;
  serving: boolean;
  failureReason: string | null;
  verification: { recordName: string; recordValue: string } | null;
  verifiedAt: string | null;
  activatedAt: string | null;
  createdAt: string;
}

export function listDomains(): Promise<{ domains: DomainView[] }> {
  return apiRequest('/v1/domains');
}

export function verifyDomain(id: string): Promise<DomainView> {
  return apiRequest(`/v1/domains/${id}/verify`, { method: 'POST' });
}

export interface FormView {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  kind: string;
  status: string;
  submissionCount: number;
  opensAt: string | null;
  closesAt: string | null;
  createdAt: string;
}

export function listForms(status?: string): Promise<{ forms: FormView[] }> {
  return apiRequest(`/v1/forms${status ? `?status=${status}` : ''}`);
}

export interface FormAnalytics {
  views: number;
  submissions: number;
  conversionRate: number;
  spam?: number;
  averageScore?: number | null;
  promoters?: number;
  passives?: number;
  detractors?: number;
  nps?: number | null;
}

export function formAnalytics(id: string): Promise<FormAnalytics> {
  return apiRequest(`/v1/forms/${id}/analytics`);
}

export type PageStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type PageKind = 'LANDING' | 'THANK_YOU' | 'CONTENT';

export interface SeoFields {
  title?: string;
  description?: string;
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

/** Exactly what `/v1/pages` sends. It was declared with a `title` and a `publishedAt` the server
    has never had, so the list rendered an empty name and an empty date. */
export interface PageView {
  id: string;
  slug: string;
  name: string;
  kind: PageKind;
  status: PageStatus;
  publishedVersionId: string | null;
  seo: SeoFields;
  funnelId: string | null;
  position: number;
  visitCount: number;
  conversionCount: number;
  ownerId: string | null;
  version: number;
  publicUrl: string | null;
}

export function listPages(status?: string): Promise<{ pages: PageView[] }> {
  return apiRequest(`/v1/pages${status ? `?status=${status}` : ''}`);
}

export interface EmailTemplateView {
  key: string;
  name: string;
  kind: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string | null;
  isOverride?: boolean;
  variables?: string[];
}

export function listEmailTemplates(): Promise<{ templates: EmailTemplateView[] }> {
  return apiRequest('/v1/email/templates');
}

export interface SaveTemplateInput {
  name: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string | null;
}

/**
 * Saves this tenant's own version of a template (Phase 133).
 *
 * A PUT with no override in place creates one; the platform text is never touched, which is what
 * makes `resetEmailTemplate` able to be a simple delete.
 */
export function saveEmailTemplate(
  key: string,
  input: SaveTemplateInput,
): Promise<EmailTemplateView> {
  return apiRequest(`/v1/email/templates/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: input,
  });
}

/** Drops this tenant's version. The platform default takes over again on the next send. */
export function resetEmailTemplate(key: string): Promise<void> {
  return apiRequest(`/v1/email/templates/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export interface TemplatePreview {
  subject: string;
  text: string;
  html: string | null;
  /** Placeholders the sample values did not fill — the ones that would arrive empty. */
  missing: string[];
}

export function previewEmailTemplate(
  key: string,
  variables: Record<string, string>,
): Promise<TemplatePreview> {
  return apiRequest(`/v1/email/templates/${encodeURIComponent(key)}/preview`, {
    method: 'POST',
    body: { variables },
  });
}

export interface EmailMessageView {
  id: string;
  templateKey: string | null;
  recipient: string;
  subject: string;
  status: string;
  attempts: number;
  failureReason: string | null;
  queuedAt: string;
  sentAt: string | null;
}

export function listEmailMessages(status?: string): Promise<{ messages: EmailMessageView[] }> {
  return apiRequest(`/v1/email/messages?limit=100${status ? `&status=${status}` : ''}`);
}

export interface SuppressionView {
  recipient: string;
  reason: string;
  detail: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export function listSuppressions(): Promise<{ suppressions: SuppressionView[] }> {
  return apiRequest('/v1/email/suppressions?limit=200');
}
